import { DynamicTypes } from "@kuika/kuika-cl-model";
import { DynamicTypeService } from "../../../services/dynamic-type-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface IDynamicTypesResult {
  bags: DynamicTypes;
  caches: DynamicTypes;
}

export interface DynamicTypesProviderState {
  isDynamicTypesLoading: boolean;
  bags: DynamicTypes;
  caches: DynamicTypes;
  getLocalDynamicTypes: (shouldLock?: boolean) => Promise<IDynamicTypesResult>;
  fetchDynamicTypeKeys: () => Promise<IDynamicTypesResult>;
}

export class DynamicTypesProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DynamicTypesProviderState
> {
  static defaultState: DynamicTypesProviderState = {
    isDynamicTypesLoading: false,
    bags: {},
    caches: {},
    getLocalDynamicTypes: () => Promise.resolve({ bags: {}, caches: {} }),
    fetchDynamicTypeKeys: () => Promise.resolve({ bags: {}, caches: {} })
  };

  getProviderValues = (): DynamicTypesProviderState => {
    return {
      isDynamicTypesLoading: this.state.isDynamicTypesLoading,
      bags: this.state.bags,
      caches: this.state.caches,
      getLocalDynamicTypes: this.getLocalDynamicTypes,
      fetchDynamicTypeKeys: this.fetchDynamicTypeKeys
    };
  };

  getLocalDynamicTypes = async (shouldLock = false): Promise<IDynamicTypesResult> => {
    if (shouldLock) this.setState({ isDynamicTypesLoading: true });
    try {
      const screenDesign = this.partial.screenProvider.getScreenDesign();
      if (!screenDesign) return { bags: {}, caches: {} };

      const screenJson = JSON.stringify(screenDesign);
      const { data } = await DynamicTypeService.GetDynamicTypesForScreen(screenJson);
      return data || { bags: {}, caches: {} };
    } catch (error) {
      console.error("Failed to get local dynamic types:", error);
      return { bags: {}, caches: {} };
    } finally {
      if (shouldLock) this.setState({ isDynamicTypesLoading: false });
    }
  };

  fetchDynamicTypeKeys = async (): Promise<IDynamicTypesResult> => {
    if (this.state.isDynamicTypesLoading) {
      return { bags: this.state.bags, caches: this.state.caches };
    }

    this.setState({ isDynamicTypesLoading: true });
    try {
      const { data } = await DynamicTypeService.GetDynamicTypes();
      const remoteBags = data?.bags || {};
      const remoteCaches = data?.caches || {};

      const localTypes = await this.getLocalDynamicTypes();
      const bags = { ...remoteBags, ...localTypes.bags };
      const caches = { ...remoteCaches, ...localTypes.caches };

      this.setState({ bags, caches });
      return { bags, caches };
    } catch (error) {
      this.setState({ bags: {}, caches: {} });
      return { bags: {}, caches: {} };
    } finally {
      this.setState({ isDynamicTypesLoading: false });
    }
  };
}
